<template>
  <div>
    <the-top-bar />
    <c-filter />
    <c-stats-row
      v-for="(item) in data"
      :key="item.index"
      :title="item.title"
      :color="color(item.index)"
      :chart-data="item.chartData"
      :chart-loading="fetchingCharts"
      :chart-smooth="item.chartSmooth"
      :chart-step-y="item.chartStepY"
      :chart-type="item.chartType"
      :total="item.total"
      :totals-loading="fetchingTotals"
    />
  </div>
</template>

<script>
import CFilter from '../components/ui/CFilter.vue'
import CStatsRow from '../components/ui/CStatsRow.vue'
import TheTopBar from '../components/layout/TheTopBar.vue'
import apiService from '../apiService'
import pastelRainbowColors from '../mixins/pastelRainbowColors'
import helpers from '../mixins/helpers'
import { Notification } from 'uiv'
import { mapGetters } from 'vuex'
import { round } from '../../../../../../resources/js/helper'

export default {
  name: 'Statistics',
  components: {
    CFilter,
    CStatsRow,
    TheTopBar
  },
  mixins: [helpers, pastelRainbowColors],
  data () {
    return {
      data: {},
      totals: {},
      fetchingCharts: false,
      fetchingTotals: false,
      watchEnabled: false
    }
  },
  computed: {
    ...mapGetters({
      chartData: 'DefaultChartData/getChartData',
      login: 'Login/getLogin',
      period: 'Period/getPeriod'
    })
  },
  watch: {
    login (newVal, oldVal) {
      if (this.watchEnabled) {
        if (oldVal !== newVal) {
          this.fetchAll()
        }
      }
    },
    period (newVal, oldVal) {
      if (this.watchEnabled) {
        if (oldVal.from !== newVal.from || oldVal.to !== newVal.to) {
          this.fetchAll()
        }
      }
    }
  },
  mounted () {
    // Инициализация данных графика
    this.data = this.chartData
    // Подгружаем данные в момент отрисовки страницы и разрешаем отслеживание состояний
    this.fetchAll()
  },
  methods: {
    /**
     * Метод возвращает следующий в списке цветовой код
     * @param index Позиция, от которой получаем следующее значение
     * @returns {string}
     */
    color (index) {
      return this.getNextColor(index)
    },
    /**
     * Включает отслеживание состояний
     */
    enableWatch () {
      if (!this.watchEnabled) {
        this.watchEnabled = true
      }
    },
    /**
     * Выполняет запросы к API на получение данных для графика и таблицы
     */
    fetchAll () {
      this.fetchCharts()
        .then(this.onChartsDataResolve)
        .then(this.enableWatch)
      this.fetchTotals()
        .then(this.onTotalsDataResolve)
        .then(this.enableWatch)
    },
    /**
     * Метод-обёртка над запросом к API /api/context/statistics/charts
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchCharts () {
      this.fetchingCharts = true
      return new Promise(resolve => {
        apiService.getStatisticsChartsData(
          this.period.from,
          this.period.to,
          this.login
        )
          .then(response => {
            this.fetchingCharts = false
            resolve(response.data)
          })
          .catch(data => {
            this.fetchingCharts = false
            Notification.notify.error({
              title: 'Ошибка',
              content: 'Не удалось загрузить диаграмму статистики.'
            })
          })
      })
    },
    /**
     * Метод-обёртка над запросом к API /api/context/statistics/totals
     * @returns {Promise<AxiosResponse<T>>}
     */
    fetchTotals () {
      this.fetchingTotals = true
      return new Promise(resolve => {
        apiService.getStatisticsTotalsData(
          this.period.from,
          this.period.to,
          this.login
        )
          .then(response => {
            this.fetchingTotals = false
            resolve(response.data)
          })
          .catch(data => {
            this.fetchingTotals = false
            Notification.notify.error({
              title: 'Ошибка',
              content: 'Не удалось загрузить информацию по итоговым данным.'
            })
          })
      })
    },
    /**
     * Метод занимается отрисовкой компонента графика
     * @param data Данные из Promise
     */
    onChartsDataResolve (data) {
      for (const prop in data) {
        this.data[prop].chartData = {
          labels: ['Яндекс'],
          charts: [
            data[prop].map(i => ({ date: i.date, value: i[prop] }))
          ]
        }
        if (data && data[prop].length > 0) {
          this.data[prop].chartStepY = this.getStepSize(
            0,
            // Максимум среди всех показателей графика
            Math.max(...data[prop].map(i => i[prop])),
            2
          )
        }
      }
    },
    /**
     * Метод занимается отрисовкой компонента с итогами
     * @param data Данные из Promise
     */
    onTotalsDataResolve (data) {
      this.data.shows.total = data.shows
      this.data.clicks.total = data.clicks
      this.data.costs.total = round(data && data.costs ? data.costs : 0).toFixed(2)
      this.data.cpc.total = round(data && data.cpc ? data.cpc : 0).toFixed(2)
      this.data.conversions.total = data.conversions
      this.data.cpl.total = round(data && data.cpl ? data.cpl : 0).toFixed(2)
    }
  }
}
</script>

<style lang="scss">
@import '../../../../../../resources/css/variables';

.c-filter,
.the-top-bar {
  margin-bottom: 30px;
}
</style>
